import { supabase } from '../lib/supabase';

class WorkflowSaveService {
  /**
   * Get all projects for selection
   */
  async getAllProjects() {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.select('id, name, status')
        ?.order('name', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching projects:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Get all modules (products) for a specific project
   */
  async getModulesByProject(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('project_products')
        ?.select('id, sku, product_name, status')
        ?.eq('project_id', projectId)
        ?.order('product_name', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching modules:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Save workflow data to a specific module in database
   * Enforces module-specific isolation
   */
  async saveWorkflowToModule(moduleId, projectId, workflowData) {
    try {
      // Prepare workflow data
      const workflowPayload = {
        nodes: workflowData?.nodes || [],
        connections: workflowData?.connections || []
      };

      const metadata = {
        ...workflowData?.metadata,
        savedAt: new Date()?.toISOString(),
        nodeCount: workflowData?.nodes?.length || 0,
        connectionCount: workflowData?.connections?.length || 0
      };

      // Check if workflow already exists for this module
      const { data: existingWorkflow, error: checkError } = await supabase
        ?.from('module_workflows')
        ?.select('id, version')
        ?.eq('module_id', moduleId)
        ?.eq('is_active', true)
        ?.single();

      if (checkError && checkError?.code !== 'PGRST116') {
        throw checkError;
      }

      let result;

      if (existingWorkflow) {
        // Update existing workflow
        const { data, error } = await supabase
          ?.from('module_workflows')
          ?.update({
            workflow_data: workflowPayload,
            metadata: metadata,
            version: existingWorkflow?.version + 1,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', existingWorkflow?.id)
          ?.select()
          ?.single();

        if (error) throw error;
        result = data;
      } else {
        // Create new workflow for this module
        const { data, error } = await supabase
          ?.from('module_workflows')
          ?.insert([{
            module_id: moduleId,
            project_id: projectId,
            workflow_name: workflowData?.metadata?.workflowName || 'Untitled Workflow',
            workflow_data: workflowPayload,
            metadata: metadata,
            version: 1,
            is_active: true
          }])
          ?.select()
          ?.single();

        if (error) throw error;
        result = data;
      }

      // Also keep localStorage backup for offline access
      const storageKey = `workflow_module_${moduleId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        moduleId,
        projectId,
        ...workflowPayload,
        metadata
      }));

      return {
        success: true,
        savedAt: metadata?.savedAt,
        workflowId: result?.id,
        version: result?.version,
        message: 'Workflow saved successfully to module'
      };
    } catch (error) {
      console.error('Error saving workflow to module:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  }

  /**
   * Load workflow data from a specific module
   * Ensures only module-specific data is loaded
   */
  async loadWorkflowFromModule(moduleId) {
    try {
      // First try to load from database
      const { data, error } = await supabase
        ?.from('module_workflows')
        ?.select('*')
        ?.eq('module_id', moduleId)
        ?.eq('is_active', true)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        return {
          success: true,
          data: {
            moduleId: data?.module_id,
            projectId: data?.project_id,
            nodes: data?.workflow_data?.nodes || [],
            connections: data?.workflow_data?.connections || [],
            metadata: {
              ...data?.metadata,
              workflowId: data?.id,
              version: data?.version,
              workflowName: data?.workflow_name
            }
          }
        };
      }

      // Fallback to localStorage if no database record
      const storageKey = `workflow_module_${moduleId}`;
      const savedData = localStorage.getItem(storageKey);

      if (!savedData) {
        return {
          success: true,
          data: null,
          message: 'No workflow data found for this module'
        };
      }

      const workflowState = JSON.parse(savedData);
      return {
        success: true,
        data: workflowState,
        source: 'localStorage'
      };
    } catch (error) {
      console.error('Error loading workflow from module:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  }

  /**
   * Get all workflows for a specific module (including version history)
   */
  async getModuleWorkflowHistory(moduleId) {
    try {
      const { data, error } = await supabase
        ?.from('module_workflows')
        ?.select('*')
        ?.eq('module_id', moduleId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching module workflow history:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Save a workflow version snapshot
   */
  async saveWorkflowVersion(workflowId, moduleId, versionData) {
    try {
      // Get current version number
      const { data: versions, error: versionError } = await supabase
        ?.from('module_workflow_versions')
        ?.select('version_number')
        ?.eq('workflow_id', workflowId)
        ?.order('version_number', { ascending: false })
        ?.limit(1);

      if (versionError) throw versionError;

      const nextVersion = versions?.length > 0 ? versions?.[0]?.version_number + 1 : 1;

      const { data, error } = await supabase
        ?.from('module_workflow_versions')
        ?.insert([{
          workflow_id: workflowId,
          module_id: moduleId,
          version_number: nextVersion,
          version_name: versionData?.name || `Version ${nextVersion}`,
          version_description: versionData?.description || '',
          workflow_data: {
            nodes: versionData?.nodes || [],
            connections: versionData?.connections || []
          },
          metadata: versionData?.metadata || {}
        }])
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        success: true,
        data,
        message: 'Version saved successfully'
      };
    } catch (error) {
      console.error('Error saving workflow version:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  }

  /**
   * Get version history for a workflow
   */
  async getWorkflowVersions(workflowId) {
    try {
      const { data, error } = await supabase
        ?.from('module_workflow_versions')
        ?.select('*')
        ?.eq('workflow_id', workflowId)
        ?.order('version_number', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching workflow versions:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Check if a module has saved workflow data
   */
  async hasWorkflowData(moduleId) {
    try {
      const { data, error } = await supabase
        ?.from('module_workflows')
        ?.select('id')
        ?.eq('module_id', moduleId)
        ?.eq('is_active', true)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking workflow data:', error);
      // Fallback to localStorage check
      const storageKey = `workflow_module_${moduleId}`;
      return localStorage.getItem(storageKey) !== null;
    }
  }

  /**
   * Get module info by ID
   */
  async getModuleById(moduleId) {
    try {
      const { data, error } = await supabase
        ?.from('project_products')
        ?.select('*')
        ?.eq('id', moduleId)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching module:', error);
      return { data: null, error: error?.message };
    }
  }

  /**
   * Delete workflow for a module
   */
  async deleteModuleWorkflow(moduleId) {
    try {
      const { error } = await supabase
        ?.from('module_workflows')
        ?.delete()
        ?.eq('module_id', moduleId);

      if (error) throw error;

      // Also clear localStorage
      const storageKey = `workflow_module_${moduleId}`;
      localStorage.removeItem(storageKey);

      return {
        success: true,
        message: 'Workflow deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting workflow:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  }
}

const workflowSaveService = new WorkflowSaveService();
export default workflowSaveService;